import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { Metamodel } from 'app/model/base/model';

import { commonCopySourceAdapts } from './copysource.adapter';
import { ServiceNowCopySourceAdapter } from './servicenow.copysource.adapter';
import {
    ServiceNowV2CopySource,
    AllOperators,
    allOperatorsMap,
    AndOrOperand,
    IAndOrTableRow,
    IConditionExpression,
    IModel,
    isLogicAppExpression,
    newQuery,
    Owned,
    QueryExpression,
    QueryExpressionType,
    QueryOperator,
    SecondaryOperator,
    AdapterContext,
    GlobalVariables
} from 'shell-models-lib';
import { CopyActivity } from 'app/model/pipeline/copy.activity';
import { isExpressionObject } from 'app/shared/utils/expression.util';
import { ValueAdapter } from '../../value.adapter';
import { ExposureControlService } from 'app/services/exposure-control/base-exposure-control.service';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(ServiceNowV2CopySource),
    asynchronous: true,
    adapts: {
        ...commonCopySourceAdapts,
        expression: '*',
        pageSize: !__IS_FABRIC_DPE__ ? '*' : undefined,
        partitionOption: GlobalVariables.enableServiceNowV2Partition ? '*' : undefined,
        selectColumns: async (context: AdapterContext) => {
            const exposureControlService = await getServiceInstance<ExposureControlService>(
                context.contextData,
                'ExposureControlService'
            );
            return exposureControlService.showSelectInServiceNowV2 ? '*' : undefined;
        },
    }
})
export class ServiceNowV2CopySourceAdapter extends ServiceNowCopySourceAdapter {
    private _curGroupId: number = 1;
    protected override model(): IModel {
        return ServiceNowV2CopySource.MODEL;
    }

    protected override async _toResource(copySource: ServiceNowV2CopySource): Promise<Model.ServiceNowV2Source> {
        let expression;
        const exposureControlService = await getServiceInstance<ExposureControlService>(
            this.context.contextData,
            'ExposureControlService'
        );
        if (ServiceNowV2CopySource.EXPRESSION.templateValue(copySource)) {
            expression = {
                type: 'Expression',
                value: ServiceNowV2CopySource.EXPRESSION.templateValue(copySource)
            };
        } else if (copySource.expression) {
            expression = this._tableToExpression(copySource.expression);
        }

        return {
            ...this._toTabularResource(copySource, this.model().name),
            expression,
            pageSize: !__IS_FABRIC_DPE__ ? ValueAdapter.toResource(copySource, ServiceNowV2CopySource.PAGESIZE) : undefined,
            partitionOption: GlobalVariables.enableServiceNowV2Partition ? ValueAdapter.toResource(copySource, ServiceNowV2CopySource.PARTITIONOPTION) : undefined,
            selectColumns: exposureControlService.showSelectInServiceNowV2 ? ValueAdapter.toResource(copySource, ServiceNowV2CopySource.SELECTCOLUMNS) : undefined
        };
    }

    protected override async _toModel(
        resource: Model.ServiceNowV2Source,
        copyActivity: CopyActivity
    ): Promise<ServiceNowV2CopySource> {
        const activity: ServiceNowV2CopySource = new ServiceNowV2CopySource(resource.name);
        const result: ServiceNowV2CopySource = this._toTabularModel(resource, copyActivity, this.model());

        if (isExpressionObject(resource.expression) || isLogicAppExpression(resource.expression)) {
            ValueAdapter.toModel(result, ServiceNowV2CopySource.EXPRESSION, resource.expression);
        } else if (resource.expression) {
            result.expression = [];
            this._expressionToTable(resource.expression, result.expression, activity);
        }

        if (!__IS_FABRIC_DPE__) {
            if (resource.pageSize !== undefined) {
                ValueAdapter.toModel(result, ServiceNowV2CopySource.PAGESIZE, resource.pageSize);
            } else {
                delete result.pageSize;
            }
        }
        if (resource.selectColumns !== undefined) {
            ValueAdapter.toModel(result, ServiceNowV2CopySource.SELECTCOLUMNS, resource.selectColumns);
        } else {
            delete result.selectColumns;
        }
        if (GlobalVariables.enableServiceNowV2Partition) {
            const isValTemplated = ValueAdapter.isTemplated(resource, ServiceNowV2CopySource.PARTITIONOPTION);
            if (isValTemplated) {
                ValueAdapter.toModel(result, ServiceNowV2CopySource.PARTITIONOPTION, resource.partitionOption);
            } else {
                result.partitionOption = resource.partitionOption;
            }
        }
        return result;
    }

    private _isAndOr(operator: AllOperators): boolean {
        return operator === AndOrOperand.and || operator === AndOrOperand.or;
    }

    private _isQueryOperator(operator: AllOperators): boolean {
        return Object.values(QueryOperator).includes(operator as QueryOperator);
    }

    private _isSecondaryOperator(operator: AllOperators): boolean {
        return Object.values(SecondaryOperator).includes(operator as SecondaryOperator);
    }

    /**
     * convert table to expression
     *  - NQ are identified by group IDs: total different groupd IDs - 1 = number of NQs
     *  - AND/OR are identified by andOr within each group
     */
    private _tableToExpression(table: QueryExpression[]): IConditionExpression {
        const groupedRows: { [key: number]: IAndOrTableRow[] } = {};
        const expArr: IConditionExpression[] = [];
        let expression: IConditionExpression;

        table?.forEach((row) => {
            const id = row.group?.id ?? 1;
            if (!groupedRows.hasOwnProperty(id)) {
                groupedRows[id] = [];
            }

            groupedRows[id].push(row);
        });

        Object.values(groupedRows).forEach((groupedTable) => {
            const groupedLen = groupedTable?.length ?? 0;
            if (groupedLen < 1) {
                this.ensureLogger().error('ServiceNowV2CopySourceAdapter', 'Grouped table cannot be empty');
                return;
            }

            const baseRow = this._rowToBaseExp(groupedTable[0]);
            if (groupedLen === 1) {
                expArr.push(baseRow);
            } else {
                const groupedExp: IConditionExpression = {
                    type: QueryExpressionType.nary,
                    operators: [],
                    operands: [baseRow]
                };
                for (let i = 1; i < groupedLen; i++) {
                    groupedExp.operators.push(groupedTable[i].andOr);
                    const base = this._rowToBaseExp(groupedTable[i]);
                    groupedExp.operands.push(base);
                }
                expArr.push(groupedExp);
            }
        });

        const len = expArr.length;
        if (len === 1) {
            expression = expArr[0];
        } else if (len < 1) {
            expression = null;
        } else {
            expression = {
                type: QueryExpressionType.nary,
                operators: new Array(len - 1).fill(newQuery),
                operands: expArr
            };
        }

        return expression;
    }

    /**
     * convert table row to base expression
     * @param row IAndOrTableRow
     * {
     *   column: value of Field
     *   operator: QueryOperator
     *   value?: value of Constant
     *   additionalValue?: value of Constant
     * }
     * @returns IConditionExpression
     * {
     *   type: Unary | Binary
     *   operators: QueryOperator
     *   operands: [
     *     {
     *       type: Field | Constant
     *       value: string
     *     }
     *   ]
     * }
     */
    private _rowToBaseExp(row: IAndOrTableRow): IConditionExpression {
        const operatorObj = allOperatorsMap.get(row.operator);
        if (!operatorObj) {
            this.ensureLogger().error('ServiceNowV2CopySourceAdapter', `Operator ${row.operator} is not supported.`);
            return undefined;
        }

        const ret: IConditionExpression = {
            type: operatorObj.hasValue ? QueryExpressionType.nary : QueryExpressionType.unary,
            operators: [operatorObj.value],
            operands: [
                {
                    type: QueryExpressionType.field,
                    value: row.column
                }
            ]
        };

        if (operatorObj.hasValue) {
            ret.operands.push({
                type: QueryExpressionType.constant,
                value: ValueAdapter.toResource(row, QueryExpression.VALUE),
            });
        }
        if (operatorObj.operator) {
            // has additional operator - ie: between
            ret.operators.push(operatorObj.operator);
            ret.operands.push({
                type: QueryExpressionType.constant,
                value: ValueAdapter.toResource(row, QueryExpression.VALUE2),
            });
        }

        return ret; // this._toQueryExpression(ret);
    }

    /**
     * convert expression to table
     */
    private _expressionToTable(expression: IConditionExpression, table: QueryExpression[], owner: Owned): void {
        let selected: AndOrOperand;
        let andOr: AndOrOperand;
        const setupBase = (exp: IConditionExpression) => {
            const row = this._toBaseRow(exp, owner);
            row.selected = selected;
            row.andOr = andOr;
            row.group = {
                id: this._curGroupId
            };
            table.push(row);
            andOr = selected;
            selected = undefined;
        };

        // recall all operators in an array belongs to the same group (NQ | andOr | query)
        const op = expression.operators[0];
        if (this._isQueryOperator(op)) {
            setupBase(expression);
        } else if (this._isAndOr(op)) {
            expression.operands.forEach((exp, idx) => {
                // since operands - 1 = operators, the last selected will be undefined
                selected = expression.operators[idx] as AndOrOperand;
                setupBase(exp);
            });
        } else if (op === newQuery) {
            expression.operands.forEach((exp) => {
                this._expressionToTable(exp, table, owner);
                this._curGroupId++;
            });
        }
    }

    /**
     * convert base expression to table row
     * @param expression - base expression, where operator must be a QueryOperator
     * {
     *   type: Unary | Binary
     *   operators: QueryOperator
     *   operands: [
     *     {
     *       type: Field | Constant
     *       value: string
     *     }
     *   ]
     * }
     * @returns QueryExpression
     * {
     *   column: value of Field
     *   operator: QueryOperator
     *   value?: value of Constant
     *   additionalValue?: value of Constant
     * }
     */
    private _toBaseRow(expression: IConditionExpression, owner: Owned): QueryExpression {
        const partialRow: QueryExpression = Owned.create<QueryExpression>(QueryExpression.MODEL, owner);
        partialRow.operator = expression.operators?.length ? expression.operators[0] as QueryOperator : undefined;

        expression.operands?.forEach((field) => {
            if (field.type === QueryExpressionType.field) {
                partialRow.column = field.value;
            } else if (field.type === QueryExpressionType.constant) {
                const isValTemplated = ValueAdapter.isTemplated(partialRow, QueryExpression.VALUE);
                if (partialRow.value == null && !isValTemplated) {
                    ValueAdapter.toModel(partialRow, QueryExpression.VALUE, field.value);
                } else {
                    ValueAdapter.toModel(partialRow, QueryExpression.VALUE2, field.value);
                }
            }
        });

        partialRow.group = {
            id: 1
        };
        return partialRow;
    }

    /**
     * Checks if expression is valid
     * @param expression - expected format:
     * {
     *   type: Unary | Binary
     *   operators: [AllOperators]
     *   operands: [QueryExpression]
     * }
     * Unary - operators.length === operands.length === 1
     * Binary - operators.length === operands.length - 1 >= 1
     * operators cannot cross-match
     * @returns true if expression is valid
     */
    private _isValidExpression(expression: IConditionExpression): boolean {
        const operandsLength = expression.operands?.length;
        const operatorsLength = expression.operators?.length;
        if (expression.type === QueryExpressionType.unary) {
            if (operandsLength !== 1 || operatorsLength !== 1) {
                return false;
            }
        } else if (expression.type === QueryExpressionType.nary) {
            if (operatorsLength < 1 || operandsLength - 1 !== operatorsLength) {
                return false;
            }
        } else {
            return false;
        }

        // check operators
        const firstOperator = expression.operators[0];
        let res: boolean = true;
        if (firstOperator === newQuery) {
            if (expression.operators.filter((op) => op !== newQuery)?.length > 0) {
                return false;
            }
            expression.operands.forEach((op) => {
                res &&= this._isValidExpression(op);
            });
        } else if (this._isAndOr(firstOperator)) {
            if (expression.operators.filter((op) => !this._isAndOr(op))?.length > 0) {
                return false;
            }
            expression.operands.forEach((op) => {
                res &&= this._isValidExpression(op);
            });
        } else {
            if (expression.operators.filter((op) => !this._isQueryOperator(op) && !this._isSecondaryOperator(op))?.length > 0) {
                return false;
            }
        }
        return res;
    }
}
